<script lang="ts">
  import { errorComponent, navigatorOptions, routes } from "@kestrel/client";
  import type { ProblemJson } from "@kestrel/http";
  import { onDestroy, setContext, type Component as ComponentType } from "svelte";
  import type { FormActions, FormResult } from "@kestrel/actions";
  import type { LoadResult } from "@kestrel/server";
  import { writable } from "svelte/store";
  import type { KestrelStoreMap, KestrelStores } from "@kestrel/stores";
  import { coerseFunctionOrValue, noop } from "../../utils/function";
  import { kestrelContext } from "./stores.js";
  import { initNavigator } from "./navigator.js";
  import { getComponent } from "./component.js";

  const {
    component,
    data,
    form,
    error,
    routeId,
    routePath,
  }: {
    component: ComponentType<any, any> | undefined;
    data?: LoadResult<(...args: any[]) => any>;
    form?: FormResult<FormActions>;
    error?: ProblemJson;
    routeId: string;
    routePath: string;
  } = $props();

  const dataStore = writable<KestrelStoreMap["data"]>(data);
  const formStore = writable(form);
  const errorStore = writable(error);
  const routeIdStore = writable<KestrelStoreMap["routeId"]>(routeId);
  const routePathStore = writable(routePath);
  const navigateStore = writable<KestrelStoreMap["navigate"]>([routeId, routePath, data, noop]);

  const stores: KestrelStores = {
    data: dataStore,
    form: formStore,
    error: errorStore,
    routeId: routeIdStore,
    routePath: routePathStore,
    navigate: navigateStore,
  };
  setContext<KestrelStores>(kestrelContext, stores);

  let Component = $state<ComponentType<any, any> | undefined>(component);

  /*                                         */
  if (!import.meta.env.SSR && navigatorOptions) {
    navigateStore.subscribe(async ([newRouteId, newRoutePath, newRouteData, complete]) => {
      if (import.meta.env.DEV && !newRouteId) {
        throw new Error(`Route id can not be undefined: ${newRouteId}`);
      }

      /*                                      */
      const component = await getComponent(newRouteId, $errorStore);
      if (Component !== component) {
        Component = component;
      }

      /*                   */
      routeIdStore.set(newRouteId);
      routePathStore.set(newRoutePath);
      dataStore.set(newRouteData);

      /*                                         */
      complete();
    });

    /*                                                            */
    onDestroy(initNavigator(stores));
  }
</script>

{#if $errorStore}
  {#await errorComponent() then { default: ErrorComponent }}
    <ErrorComponent error={$errorStore} />
  {/await}
{:else if Component}
  <Component data={$dataStore} form={$formStore} />
{/if}
